<template>
  <div
    class="flex items-center text-base font-medium"
    data-testid="delivery-info"
  >
    <div
      class="mr-2 rounded-full"
      :class="[iconSizeClasses, colorClass]"
      :data-testid="cypressId"
    ></div>
    <span>
      {{ message }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { Product, Stock } from '@scayle/storefront-core'
import {
  PRODUCT_LIFECYCLE_STATUS_PUBLISHED,
  type ProductLifecycleStatus,
} from '~/constants/product'

const props = defineProps({
  stock: {
    type: Object as PropType<Stock>,
    default: () => undefined,
  },
  iconSize: {
    type: Number as PropType<number>,
    default: 5,
    validator: (value: number) => [2.5, 5].includes(value),
  },
  isRxGlasses: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  salesStatus: {
    type: String as PropType<ProductLifecycleStatus>,
    default: PRODUCT_LIFECYCLE_STATUS_PUBLISHED,
  },
  product: {
    type: Object as PropType<Product>,
    default: () => undefined,
  },
})

const { data: customData } = useCustomData()
const { $i18n } = useNuxtApp()

const stock = toRef(props, 'stock')

const iconSizeClasses = computed(() => {
  const classes: Record<number, string> = {
    2.5: 'w-2.5 h-2.5',
    5: 'w-5 h-5',
  }
  return classes[props.iconSize]
})

const soldOut = {
  colorClass: 'bg-neutral-50',
  cypressId: 'delivery-info-gray',
  message: $i18n.t('pdp.out_of_stock_delivery_info'),
}

const data = computed(() => {
  if (props.salesStatus !== PRODUCT_LIFECYCLE_STATUS_PUBLISHED) {
    return soldOut
  }

  const quantity = stock.value?.quantity ?? 0
  const sellableWithoutStock = !quantity && stock.value?.isSellableWithoutStock
  const sellableWithoutStockTotalTime =
    customData.value?.deliveryEstimatesProductType
      ?.sellableWithoutStockTotalTime

  if (sellableWithoutStock && sellableWithoutStockTotalTime) {
    return {
      colorClass: 'bg-amber-500',
      cypressId: 'delivery-info-orange',
      message: $i18n.t('pdp.delivery_in', {
        value: sellableWithoutStockTotalTime,
      }),
    }
  }

  const productEstimates =
    props.product &&
    customData.value?.deliveryEstimatesProductType?.productType?.[
      getProductType(props.product) ?? ''
    ]

  if (productEstimates && quantity > 0) {
    return {
      colorClass: 'bg-emerald-500',
      cypressId: props.isRxGlasses
        ? 'delivery-info-rx'
        : 'delivery-info-orange',
      message: $i18n.t('pdp.delivery_in', {
        value: productEstimates,
      }),
    }

    // Fallback in case customData deliveryEstimatesProductType are not set
  } else if (props.isRxGlasses && (quantity > 0 || sellableWithoutStock)) {
    return {
      colorClass: 'bg-emerald-500',
      cypressId: 'delivery-info-rx',
      message: $i18n.t('pdp.delivery_forecast_rx'),
    }
  } else if (quantity > 0) {
    return {
      colorClass: 'bg-emerald-500',
      cypressId: 'delivery-info-green',
      message: $i18n.t('pdp.delivery_forecast_in_stock'),
    }
  } else if (sellableWithoutStock) {
    return {
      colorClass: 'bg-amber-500',
      cypressId: 'delivery-info-orange',
      message: $i18n.t('pdp.delivery_forecast_out_of_stock'),
    }
  } else {
    return soldOut
  }
})

const message = computed(() => data.value.message)
const cypressId = computed(() => data.value.cypressId)
const colorClass = computed(() => data.value.colorClass)
</script>
